import { PrintCardContentViewModel } from '../../shared/print-card-content-view-model';
import { Injectable } from '@angular/core';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { ProductType } from '../../../../../../../models/enum/dto/product-type.enum';
import { Move, SortUtils } from '../../../../../../../utils/sort-utils';
import type { PopsCannabisCardData } from '../../../../../../../models/print-cards/pops-cannabis-card-data';
import type { CannabinoidOrTerpeneFromPool, PropertyPoolSorter } from '../../shared/card-data-with-property-pooling';
import type { BadgeAsset } from '../../../../../../../models/image/dto/badge-asset';
import { SectionColumnTerpeneType } from '../../../../../../../models/enum/shared/section-column-terpene-type';
import { SectionColumnConfigDataBadgeMode } from '../../../../../../../models/enum/shared/section-column-config-data-value';
import { PrintCardBadgeRowUtils } from '../../../../../../../utils/print-card-badge-row-utils';
import { exists } from '../../../../../../../functions/exists';

@Injectable()
export class PopsCannabis5By5CardStackContentViewModel extends PrintCardContentViewModel {
  public readonly cardData$: Observable<PopsCannabisCardData>;

  // Hard cap before the badge row's fit directive trims further for space.
  private readonly maxListTerpeneBadges = 3;

  public readonly priceStreamOverride$ = this.cardData$.pipe(
    map(cardData => cardData?.getOverridePriceStreamForLabelComponent())
  );

  public readonly leftSideLargeText$ = this.cardData$.pipe(map(cardData => cardData?.getLeftLargeText()));

  public readonly showLeftSideLargeText$ = combineLatest([this.preview$, this.cardData$]).pipe(
    map(([previewMode, cardData]) => {
      const productType = cardData?.rowProductType();
      const hasSticker =
        productType === ProductType.Flower ||
        productType === ProductType.Vape ||
        productType === ProductType.Concentrate;
      return previewMode || !hasSticker;
    })
  );

  public readonly totalCompoundCharacters$ = this.propertiesBasedOnPools$.pipe(
    map(properties => {
      return properties?.reduce((acc, prop) => acc + prop?.value?.length, 0) || 0;
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  public readonly labelRowBadgeAssets$ = this.cardData$.pipe(
    map(cardData => {
      const productBadges = cardData?.getAllVariantBadges() ?? [];
      const terpeneBadges = this.getListTerpeneBadgeAssets(cardData);
      return PrintCardBadgeRowUtils.appendListTerpeneBadges(productBadges, terpeneBadges, this.maxListTerpeneBadges);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly hasLabelRowBadges$ = this.labelRowBadgeAssets$.pipe(
    map(badgeAssets => badgeAssets?.length > 0),
    distinctUntilChanged()
  );

  // Must stay below hasLabelRowBadges$ (useDefineForClassFields: false runs initializers top to bottom).
  public override badgeOrLabelVisible$ = combineLatest([this.hasLabelRowBadges$, this.labelText$]).pipe(
    map(([hasLabelRowBadges, labelText]) => hasLabelRowBadges || exists(labelText))
  );

  /**
   * The POPs card always behaves as List Terpenes = On in Badge mode; the card stack's column config is ignored.
   * getBadgeModeAssetsForColumnType dereferences the mapping without a null check, so bail when it is missing.
   */
  private getListTerpeneBadgeAssets(cardData: PopsCannabisCardData): BadgeAsset[] {
    const listTerpenes = SectionColumnTerpeneType.ListTerpenes;
    const hasListTerpeneMapping = cardData?.menu?.badgeMappings?.some(mapping => mapping?.categoryKey === listTerpenes);
    if (!hasListTerpeneMapping) return [];
    const badgeMode = SectionColumnConfigDataBadgeMode.Badge;
    return (cardData.getListItemsForColumnType(listTerpenes, badgeMode) as BadgeAsset[]) ?? [];
  }

  maxCompoundContainerWidth$(compound: CannabinoidOrTerpeneFromPool): Observable<number> {
    return this.totalCompoundCharacters$.pipe(
      map(totalCompoundCharacters => {
        const maxWidth = 301.22;
        const allocatedPercentage = (compound?.value?.length ?? 0) / totalCompoundCharacters;
        return Math.floor(allocatedPercentage * maxWidth);
      }),
      distinctUntilChanged()
    );
  }

  /**
   * Sort final property list from highest to lowest value, then by title.
   * Force "terps" to end of list if present.
   */
  override finalSortLambdaForCompoundProperties(): PropertyPoolSorter | null {
    return (a: CannabinoidOrTerpeneFromPool, b: CannabinoidOrTerpeneFromPool): number => {
      const terpsToEnd = (aTitle: string, bTitle: string): Move => {
        if (aTitle?.toLowerCase() === 'terps') return Move.ARight;
        if (bTitle?.toLowerCase() === 'terps') return Move.BRight;
        return 0;
      };
      return (
        terpsToEnd(a?.title, b?.title) ||
        SortUtils.parseFloatFromStringDescending(a?.value, b?.value) ||
        SortUtils.stringAscending(a?.title, b?.title)
      );
    };
  }
}
